import React from 'react';
import Text, { TextTag, type TextVariant, type TextProps, TEXT_VARIANT } from '@pixum/text';
import { type RichTextDocument, RichTextContent } from './RichTextContent';

type RichTextBodyProps = Omit<TextProps, 'children' | 'tag'> & {
  text?: RichTextDocument;
  wrapWithText?: boolean;
  wrapTextTag?: TextTag;
  wrapTextVariant?: TextVariant;
};

/**
 * The `RichTextBody` component is responsible for rendering rich text content.
 *
 * Props:
 * - `text` (string): The rich text content to be rendered. If no text is provided, the component will return `undefined`.
 *
 * Functionality:
 * - If `text` is provided, it renders the rich text content using the `RichTextContent` function.
 * - The transformed content is then rendered inside a `Text` component with a `span` tag.
 * - The `RichTextContent` function ensures the content is properly processed and cast to a `ReactNode` type.
 */
export function RichTextBody({
  text,
  wrapWithText = true,
  wrapTextTag = TextTag.SPAN,
  wrapTextVariant = TEXT_VARIANT.BODY,
  ...textProps
}: RichTextBodyProps) {
  if (wrapWithText) {
    return text ? (
      <Text tag={wrapTextTag} variant={wrapTextVariant} {...textProps}>
        <RichTextContent node={text} />
      </Text>
    ) : undefined;
  }

  return text ? <RichTextContent node={text} /> : undefined;
}
